import {FC, useMemo} from 'react';
import {observer} from 'mobx-react';
import {clamp} from 'lodash';
import {Icon, Input, Message, Popup} from 'semantic-ui-react';
import {Field} from 'apstra-ui-common';

import {useRackEditorStore} from '../hooks/useRackEditorStore';
import NodePropertiesStore from '../store/NodePropertiesStore';
import TagsControl from '../../components/TagsControl';
import AvailablePorts from './AvailablePorts';
import PortsCount from './PortsCount';
import NumberInput from './NumberInput';
import PortChannelIdRange from './PortChannelIdRange';
import {Checkbox, DropdownControl} from '../../exports';
import {REDUNDANCY, maxVlanId} from '../const';

import Node from '../store/Node';

import './NodeProperties.less';
import {SpeedType} from '../types';

const NodeProperties: FC<{nodes: Node[]}> = ({nodes}) => {
  const rackEditorStore = useRackEditorStore();
  const {rackStore} = rackEditorStore;
  const {devices, availableTags, isL3Clos, isRailCollapsed} = rackStore;

  const nodePropertiesStore = useMemo(
    () => {
      return new NodePropertiesStore(rackEditorStore, nodes);
    },
    [rackEditorStore, nodes]
  );

  const {deviceOptions, closeOnEnter, isSwitch, isBatch, noLogicalDevice, result, spinePorts, allMlagPairs,
    portChannelError, allPortChannelsSet, trackChange, changeRedundancy, setPortChannel, canSwitchToMlag,
    canSwitchToEsi, maxSpineLinks, spinePortsExist, haveRails, changeLogicalDevice, allDevicesAreVisible,
    portChannelsNeeded, portChannelId, portChannelIdExt, portChannelRangeError} = nodePropertiesStore!;

  const {role, redundancyProtocol, _label, logicalDevice, tags, mlagVlanId, spineLinksCount, spineLinksSpeed,
    labelNotUnique, isLeaf} = result || {};

  let pcidRangeError = '';
  if (portChannelRangeError) {
    pcidRangeError = 'Invalid range';
  } else if (!allPortChannelsSet && (isBatch || result.myPortChannelIds.length > 0)) {
    pcidRangeError = 'Insufficient range';
  }

  return (
    <div className='re-node-properties'>
      <Field
        required
        label='Label'
      >
        <Input
          fluid
          value={_label || ''}
          onChange={(event, {value}) => trackChange('_label', value)}
          onKeyDown={closeOnEnter}
          error={labelNotUnique || (!isBatch && !_label)}
          maxLength={64}
          placeholder={isBatch ? 'Multiple values' : ''}
        />
      </Field>
      {!isBatch && <AvailablePorts node={result} />}
      <Field
        required
        label={
          <>
            {'Logical Device'}
            {!allDevicesAreVisible &&
              <Popup
                trigger={<Icon name='info circle' />}
                inverted
                position='top center'
              >
                {'Only applicable logical devices (with amount/speeds of ports, sufficient to cover server\' and its '}
                {'links requirements) are listed.'}
              </Popup>

            }
          </>
        }
      >
        <DropdownControl
          fluid
          selection
          search
          value={logicalDevice?.id}
          onChange={(value) => changeLogicalDevice(devices[value], spineLinksSpeed)}
          options={deviceOptions}
          onKeyDown={closeOnEnter}
          placeholder={(isBatch && !logicalDevice) ? 'Multiple values' : 'Not set'}
        />
      </Field>
      <Field label='Tags'>
        <TagsControl
          className='re-node-tags'
          fluid
          value={tags}
          onChange={(value: string[]) => trackChange('tags', [...value])}
          knownTags={availableTags}
        />
      </Field>
      {
        isLeaf && isL3Clos &&
          <div className='spine-links'>
            <h6 className='required'>{'Links per spine'}</h6>
            {spinePortsExist &&
              <div>
                <PortsCount
                  portsBySpeed={spinePorts}
                  showEmpty
                  selectedSpeed={spineLinksSpeed}
                  error={!spineLinksSpeed}
                  onClick={(speed: SpeedType, speedString?: string) => {
                    trackChange(
                      'spineLinksCount',
                      clamp(spineLinksCount, 0, spinePorts?.[speedString!] ?? 0)
                    );
                    trackChange('spineLinksSpeed', speedString);
                  }}
                  usedCount={+spineLinksCount}
                />
                <Field
                  className='count'
                  required
                  label='Count'
                  disabled={!spineLinksSpeed}
                >
                  <NumberInput
                    type='number'
                    fluid
                    value={+spineLinksCount || 0}
                    min={0}
                    max={maxSpineLinks}
                    onChange={(event, {value}) => trackChange(
                      'spineLinksCount',
                      clamp(value, 0, maxSpineLinks)
                    )}
                    error={!spineLinksCount}
                  />
                </Field>
              </div>
            }
            {!spinePortsExist &&
              <Message error content='No free ports with role "spine"' />
            }
          </div>
      }
      {
        (isSwitch && !isBatch && !isRailCollapsed && !haveRails) &&
          <Field label='Redundancy Protocol' required className='redundancies'>
            <Checkbox
              radio
              name='redundancy'
              label='None'
              checked={!redundancyProtocol}
              onChange={changeRedundancy}
              disabled={noLogicalDevice}
            />
            {isLeaf && isL3Clos &&
              <Checkbox
                radio
                name='redundancy'
                label='MLAG'
                value={REDUNDANCY.MLAG}
                checked={redundancyProtocol === REDUNDANCY.MLAG}
                onChange={changeRedundancy}
                disabled={!canSwitchToMlag}
              />
            }
            <Checkbox
              radio
              name='redundancy'
              label='ESI'
              value={REDUNDANCY.ESI}
              checked={redundancyProtocol === REDUNDANCY.ESI}
              onChange={changeRedundancy}
              disabled={!canSwitchToEsi}
            />
          </Field>
      }
      {
        allMlagPairs &&
          <Field label='MLAG Keepalive VLAN ID' required>
            <NumberInput
              type='number'
              fluid
              value={mlagVlanId}
              min={0}
              max={maxVlanId}
              onChange={(event, {value}) => trackChange('mlagVlanId', value, true)}
              error={!mlagVlanId}
            />
          </Field>
      }
      {
        role && !isSwitch &&
          <>
            <label>{`Port Channel IDs Range (min. ${portChannelsNeeded}):`}</label>
            {!!pcidRangeError &&
              <Message error content={pcidRangeError} />
            }
            <div className='re-port-channel-range'>
              <PortChannelIdRange
                minValue={portChannelId}
                maxValue={portChannelIdExt}
                asRange
                onChange={setPortChannel}
                error={portChannelError || !!pcidRangeError}
                node={result}
              />
            </div>
          </>
      }
    </div>
  );
};

export default observer(NodeProperties);
